import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Select from "react-select";
import { classList, subjectList } from "../../utils";
import { server } from "../../server";

/**
 * LibraryReports
 *
 * Mirrors StudentReports / FinanceReports layout exactly:
 *  Left col  → Hero + Dataset selector + Filters + Live Snapshot
 *  Right col → Report panel (category tabs + report buttons + quick action cards)
 *
 * Datasets:
 *  1. Physical Books – library_data_mongodb        (BookList)
 *  2. E-Books        – online_library_data_mongodb  (EBookList)
 *  3. Issued Books   – book issue/return records
 *  4. Low Stock      – books with low availableQuantity
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
const toUpper = (v) =>
  v !== null && v !== undefined ? String(v).toUpperCase() : "";

const today = new Date().toISOString().split("T")[0];

const writeWorkbook = (wb, filename) => {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
  toast.success("Report Generated");
};

const LOW_STOCK_THRESHOLD = 3;

// ─── dataset meta ─────────────────────────────────────────────────────────────
const DATASETS = {
  physical: { label: "Physical Books", color: "success" },
  ebooks: { label: "E-Books", color: "info" },
  issued: { label: "Issued Books", color: "warning" },
  lowstock: { label: "Low Stock", color: "danger" },
};

// ─── report categories ────────────────────────────────────────────────────────
const REPORT_CATEGORIES = [
  { key: "all", label: "All Reports" },
  { key: "books", label: "Books" },
  { key: "ebooks", label: "E-Books" },
  { key: "issued", label: "Issued / Return" },
  { key: "stock", label: "Stock" },
  { key: "analytics", label: "Analytics" },
];

// ─────────────────────────────────────────────────────────────────────────────
function LibraryReports() {
  // ── raw data ───────────────────────────────────────────────────────────────
  const [booksData, setBooksData] = useState([]);
  const [eBooksData, setEBooksData] = useState([]);
  const [issuedData, setIssuedData] = useState([]);

  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingEBooks, setLoadingEBooks] = useState(false);
  const [loadingIssued, setLoadingIssued] = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeDataset, setActiveDataset] = useState("physical");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedLang, setSelectedLang] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── fetches ────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Physical books
    setLoadingBooks(true);
    fetch(`${server}/library_data_mongodb`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
      },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((d) => setBooksData(d?.["body-json"]?.body?.data || []))
      .catch(console.error)
      .finally(() => setLoadingBooks(false));

    // E-Books
    setLoadingEBooks(true);
    fetch(`${server}/online_library_data_mongodb`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((d) => setEBooksData(d?.["body-json"]?.body?.data || []))
      .catch(console.error)
      .finally(() => setLoadingEBooks(false));

    // Issued books
    setLoadingIssued(true);
    fetch(`${server}/getAllBookIssueReturn_mongodb`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => {
        const data = d?.["body-json"]?.body?.data || d?.data || [];
        setIssuedData(
          Array.isArray(data)
            ? data.sort(
                (a, b) =>
                  new Date(b.issueDate || b.createdAt) -
                  new Date(a.issueDate || a.createdAt),
              )
            : [],
        );
      })
      .catch(console.error)
      .finally(() => setLoadingIssued(false));
  }, []);

  // ── derived datasets ───────────────────────────────────────────────────────
  const lowStockBooks = useMemo(
    () =>
      booksData.filter(
        (b) => Number(b.availableQuantity || 0) <= LOW_STOCK_THRESHOLD,
      ),
    [booksData],
  );

  const datasetMap = {
    physical: { data: booksData, loading: loadingBooks },
    ebooks: { data: eBooksData, loading: loadingEBooks },
    issued: { data: issuedData, loading: loadingIssued },
    lowstock: { data: lowStockBooks, loading: false },
  };

  const currentRaw = datasetMap[activeDataset]?.data || [];
  const currentLoading = datasetMap[activeDataset]?.loading || false;

  // ── dynamic filter options ─────────────────────────────────────────────────
  const bookTypeOptions = useMemo(() => {
    const set = new Set(
      [...booksData, ...eBooksData].map((b) => b.bookType).filter(Boolean),
    );
    return Array.from(set).map((t) => ({ value: t, label: t }));
  }, [booksData, eBooksData]);

  const languageOptions = useMemo(() => {
    const set = new Set(
      [...booksData, ...eBooksData].map((b) => b.language).filter(Boolean),
    );
    return Array.from(set).map((l) => ({ value: l, label: l }));
  }, [booksData, eBooksData]);

  const classOptions = classList.map((c) => ({ value: c, label: c }));
  const subjectOptions = subjectList.map((s) => ({ value: s, label: s }));

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...currentRaw];

    if (selectedClass) data = data.filter((b) => b.class === selectedClass);
    if (selectedSubject)
      data = data.filter((b) => b.subject === selectedSubject);
    if (selectedType) data = data.filter((b) => b.bookType === selectedType);
    if (selectedLang) data = data.filter((b) => b.language === selectedLang);

    if (fromDate || toDate) {
      data = data.filter((b) => {
        const d = (b.createdAt || b.issueDate || "")?.slice(0, 10);
        if (!d) return false;
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
        return true;
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const flatten = (obj) => {
        const vals = [];
        for (const k in obj) {
          const v = obj[k];
          if (v && typeof v === "object") vals.push(...flatten(v));
          else if (typeof v === "string") vals.push(v);
        }
        return vals;
      };
      data = data.filter((b) =>
        flatten(b).some((v) => v.toLowerCase().includes(lower)),
      );
    }

    return data;
  }, [
    currentRaw,
    selectedClass,
    selectedSubject,
    selectedType,
    selectedLang,
    fromDate,
    toDate,
    searchTerm,
  ]);

  // ── live snapshot ──────────────────────────────────────────────────────────
  const snapshot = useMemo(() => {
    const totalPhysical = booksData.length;
    const totalQty = booksData.reduce(
      (s, b) => s + Number(b.totalQuantity || 0),
      0,
    );
    const availableQty = booksData.reduce(
      (s, b) => s + Number(b.availableQuantity || 0),
      0,
    );
    const issuedQty = totalQty - availableQty;
    const totalEBooks = eBooksData.length;
    const totalIssued = issuedData.length;
    const returnedIssued = issuedData.filter(
      (i) => i.returnDate || i.status?.toLowerCase() === "returned",
    ).length;
    const overdueIssued = issuedData.filter((i) => {
      const due = i.dueDate || i.returnDate;
      return due && due < today && !(i.status?.toLowerCase() === "returned");
    }).length;

    return {
      totalPhysical,
      totalQty,
      availableQty,
      issuedQty,
      totalEBooks,
      totalIssued,
      returnedIssued,
      overdueIssued,
      lowStock: lowStockBooks.length,
    };
  }, [booksData, eBooksData, issuedData, lowStockBooks]);

  // ── select styles ──────────────────────────────────────────────────────────
  const selectStyles = {
    control: (b) => ({
      ...b,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (b) => ({ ...b, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
    menu: (b) => ({
      ...b,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (b, s) => ({
      ...b,
      backgroundColor: s.isSelected
        ? "#ffc107"
        : s.isFocused
          ? "#f8f9fa"
          : "white",
      color: s.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (b) => ({ ...b, color: "#6c757d", fontSize: "14px" }),
    singleValue: (b) => ({ ...b, color: "#212529", fontSize: "14px" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ROW FORMATTERS
  // ─────────────────────────────────────────────────────────────────────────
  const formatBookRow = (b) => ({
    "BOOK ID": toUpper(b.bookId || ""),
    "BOOK NO": toUpper(b.bookNo || ""),
    "BOOK TYPE": toUpper(b.bookType || ""),
    TITLE: toUpper(b.title || ""),
    SUBJECT: toUpper(b.subject || ""),
    CLASS: toUpper(b.class || ""),
    LANGUAGE: toUpper(b.language || ""),
    AUTHOR: toUpper(b.author || ""),
    PUBLISHER: toUpper(b.publisher || ""),
    "TOTAL QTY": Number(b.totalQuantity || 0),
    "AVAILABLE QTY": Number(b.availableQuantity || 0),
    "ISSUED QTY":
      Number(b.totalQuantity || 0) - Number(b.availableQuantity || 0),
    "BILL NO": toUpper(b.purchase?.billNo || ""),
    "BILL DATE": b.purchase?.billDate
      ? new Date(b.purchase.billDate).toLocaleDateString("en-GB")
      : "",
    "PURCHASED FROM": toUpper(b.purchase?.purchasedFrom || ""),
    "DATE ADDED": b.createdAt
      ? new Date(b.createdAt).toLocaleDateString("en-GB")
      : "",
  });

  const formatEBookRow = (b) => ({
    "E-BOOK ID": toUpper(b.bookId || ""),
    "E-BOOK NO": toUpper(b.bookNo || ""),
    "E-BOOK TYPE": toUpper(b.bookType || ""),
    TITLE: toUpper(b.title || ""),
    SUBJECT: toUpper(b.subject || ""),
    CLASS: toUpper(b.class || ""),
    LANGUAGE: toUpper(b.language || ""),
    AUTHOR: toUpper(b.author || ""),
    PUBLISHER: toUpper(b.publisher || ""),
    DESCRIPTION: b.description || "",
    "FILE URL": b.file || "",
    "DATE ADDED": b.createdAt
      ? new Date(b.createdAt).toLocaleDateString("en-GB")
      : "",
  });

  const formatIssuedRow = (i) => ({
    "ISSUE ID": toUpper(i.issueId || i._id || ""),
    "BOOK ID": toUpper(i.bookId || ""),
    "BOOK TITLE": toUpper(i.bookTitle || i.title || ""),
    "ADMISSION NO": toUpper(i.admission_no || ""),
    "STUDENT NAME": toUpper(i.studentName || i.name || ""),
    CLASS: toUpper(i.class || ""),
    "ISSUE DATE": i.issueDate
      ? new Date(i.issueDate).toLocaleDateString("en-GB")
      : "",
    "DUE DATE": i.dueDate
      ? new Date(i.dueDate).toLocaleDateString("en-GB")
      : "",
    "RETURN DATE": i.returnDate
      ? new Date(i.returnDate).toLocaleDateString("en-GB")
      : "",
    STATUS: toUpper(i.status || ""),
    FINE: String(i.fine || "0"),
    REMARK: i.remark || "",
  });

  // get formatter for active dataset
  const getFormatter = () => {
    if (activeDataset === "ebooks") return formatEBookRow;
    if (activeDataset === "issued") return formatIssuedRow;
    return formatBookRow;
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  GENERIC HELPERS
  // ─────────────────────────────────────────────────────────────────────────
  const groupWise = (data, keyFn, filename, formatter) => {
    if (!data.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    data.forEach((r) => {
      const key = toUpper(keyFn(r) || "UNKNOWN");
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatter(r));
    });
    Object.entries(groups).forEach(([key, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        key.slice(0, 31),
      );
    });
    writeWorkbook(wb, `${filename}_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  PHYSICAL BOOK REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportBooksAll = () => {
    if (!booksData.length) {
      toast.error("No book data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(booksData.map(formatBookRow)),
      "ALL BOOKS",
    );
    writeWorkbook(wb, `Library_AllBooks_${today}.xlsx`);
  };
  const reportBooksClassWise = () =>
    groupWise(
      booksData,
      (b) => b.class,
      "Library_Books_ClassWise",
      formatBookRow,
    );
  const reportBooksSubjectWise = () =>
    groupWise(
      booksData,
      (b) => b.subject,
      "Library_Books_SubjectWise",
      formatBookRow,
    );
  const reportBooksTypeWise = () =>
    groupWise(
      booksData,
      (b) => b.bookType,
      "Library_Books_TypeWise",
      formatBookRow,
    );
  const reportBooksPublisher = () =>
    groupWise(
      booksData,
      (b) => b.publisher,
      "Library_Books_PublisherWise",
      formatBookRow,
    );
  const reportBooksAuthor = () =>
    groupWise(
      booksData,
      (b) => b.author,
      "Library_Books_AuthorWise",
      formatBookRow,
    );
  const reportBooksLanguage = () =>
    groupWise(
      booksData,
      (b) => b.language,
      "Library_Books_LanguageWise",
      formatBookRow,
    );

  const reportBooksAvailable = () => {
    const list = booksData.filter((b) => Number(b.availableQuantity || 0) > 0);
    if (!list.length) {
      toast.error("No available books found");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBookRow)),
      "AVAILABLE",
    );
    writeWorkbook(wb, `Library_AvailableBooks_${today}.xlsx`);
  };

  const reportBooksFullyIssued = () => {
    const list = booksData.filter(
      (b) => Number(b.availableQuantity || 0) === 0,
    );
    if (!list.length) {
      toast.error("No fully-issued books found");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatBookRow)),
      "FULLY ISSUED",
    );
    writeWorkbook(wb, `Library_FullyIssued_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  E-BOOK REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportEBooksAll = () => {
    if (!eBooksData.length) {
      toast.error("No e-book data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(eBooksData.map(formatEBookRow)),
      "ALL E-BOOKS",
    );
    writeWorkbook(wb, `Library_AllEBooks_${today}.xlsx`);
  };
  const reportEBooksClassWise = () =>
    groupWise(
      eBooksData,
      (b) => b.class,
      "Library_EBooks_ClassWise",
      formatEBookRow,
    );
  const reportEBooksSubjectWise = () =>
    groupWise(
      eBooksData,
      (b) => b.subject,
      "Library_EBooks_SubjectWise",
      formatEBookRow,
    );
  const reportEBooksTypeWise = () =>
    groupWise(
      eBooksData,
      (b) => b.bookType,
      "Library_EBooks_TypeWise",
      formatEBookRow,
    );
  const reportEBooksLanguage = () =>
    groupWise(
      eBooksData,
      (b) => b.language,
      "Library_EBooks_LangWise",
      formatEBookRow,
    );

  // ─────────────────────────────────────────────────────────────────────────
  //  ISSUED / RETURN REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportIssuedAll = () => {
    if (!issuedData.length) {
      toast.error("No issue data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(issuedData.map(formatIssuedRow)),
      "ALL ISSUED",
    );
    writeWorkbook(wb, `Library_IssuedAll_${today}.xlsx`);
  };
  const reportIssuedToday = () => {
    const list = issuedData.filter(
      (i) => (i.issueDate || i.createdAt || "")?.slice(0, 10) === today,
    );
    if (!list.length) {
      toast.error("No books issued today");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatIssuedRow)),
      "ISSUED TODAY",
    );
    writeWorkbook(wb, `Library_IssuedToday_${today}.xlsx`);
  };
  const reportIssuedPending = () => {
    const list = issuedData.filter(
      (i) => !i.returnDate && (i.status || "").toLowerCase() !== "returned",
    );
    if (!list.length) {
      toast.error("No pending returns");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatIssuedRow)),
      "PENDING RETURN",
    );
    writeWorkbook(wb, `Library_PendingReturn_${today}.xlsx`);
  };
  const reportIssuedReturned = () => {
    const list = issuedData.filter(
      (i) => i.returnDate || (i.status || "").toLowerCase() === "returned",
    );
    if (!list.length) {
      toast.error("No returned records");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatIssuedRow)),
      "RETURNED",
    );
    writeWorkbook(wb, `Library_Returned_${today}.xlsx`);
  };
  const reportIssuedOverdue = () => {
    const list = issuedData.filter((i) => {
      const due = i.dueDate;
      return due && due < today && !(i.status?.toLowerCase() === "returned");
    });
    if (!list.length) {
      toast.error("No overdue books");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatIssuedRow)),
      "OVERDUE",
    );
    writeWorkbook(wb, `Library_Overdue_${today}.xlsx`);
  };
  const reportIssuedClassWise = () =>
    groupWise(
      issuedData,
      (i) => i.class,
      "Library_Issued_ClassWise",
      formatIssuedRow,
    );
  const reportIssuedDateRange = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From and To dates");
      return;
    }
    const list = issuedData.filter((i) => {
      const d = (i.issueDate || i.createdAt || "")?.slice(0, 10);
      return d && d >= fromDate && d <= toDate;
    });
    if (!list.length) {
      toast.error("No records in this date range");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatIssuedRow)),
      "DATE RANGE",
    );
    writeWorkbook(wb, `Library_Issued_${fromDate}_to_${toDate}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  STOCK REPORTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportLowStock = () => {
    if (!lowStockBooks.length) {
      toast.error("No low-stock books");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(lowStockBooks.map(formatBookRow)),
      "LOW STOCK",
    );
    writeWorkbook(wb, `Library_LowStock_${today}.xlsx`);
  };

  const reportStockSummary = () => {
    if (!booksData.length) {
      toast.error("No book data");
      return;
    }
    const groups = {};
    booksData.forEach((b) => {
      const cls = b.class || "UNKNOWN";
      if (!groups[cls])
        groups[cls] = {
          Class: cls,
          Titles: 0,
          "Total Qty": 0,
          "Available Qty": 0,
          "Issued Qty": 0,
        };
      groups[cls].Titles++;
      groups[cls]["Total Qty"] += Number(b.totalQuantity || 0);
      groups[cls]["Available Qty"] += Number(b.availableQuantity || 0);
      groups[cls]["Issued Qty"] +=
        Number(b.totalQuantity || 0) - Number(b.availableQuantity || 0);
    });
    const rows = Object.values(groups);
    const totals = rows.reduce(
      (acc, r) => {
        acc.Titles += r.Titles;
        acc["Total Qty"] += r["Total Qty"];
        acc["Available Qty"] += r["Available Qty"];
        acc["Issued Qty"] += r["Issued Qty"];
        return acc;
      },
      {
        Class: "TOTAL",
        Titles: 0,
        "Total Qty": 0,
        "Available Qty": 0,
        "Issued Qty": 0,
      },
    );
    rows.push(totals);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Stock Summary",
    );
    writeWorkbook(wb, `Library_StockSummary_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ANALYTICS
  // ─────────────────────────────────────────────────────────────────────────
  const reportLibrarySummary = () => {
    const wb = XLSX.utils.book_new();
    const summary = [
      {
        MODULE: "Physical Books",
        TITLES: booksData.length,
        "TOTAL QTY": snapshot.totalQty,
        AVAILABLE: snapshot.availableQty,
        ISSUED: snapshot.issuedQty,
      },
      {
        MODULE: "E-Books",
        TITLES: eBooksData.length,
        "TOTAL QTY": "N/A",
        AVAILABLE: "N/A",
        ISSUED: "N/A",
      },
      {
        MODULE: "Book Issues",
        TITLES: issuedData.length,
        "TOTAL QTY": snapshot.returnedIssued,
        AVAILABLE: "Returned",
        ISSUED: snapshot.overdueIssued,
      },
      {
        MODULE: "Low Stock",
        TITLES: lowStockBooks.length,
        "TOTAL QTY": "—",
        AVAILABLE: "—",
        ISSUED: `≤${LOW_STOCK_THRESHOLD} copies`,
      },
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(summary),
      "Library Summary",
    );
    writeWorkbook(wb, `Library_Summary_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    const wb = XLSX.utils.book_new();
    if (booksData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(booksData.map(formatBookRow)),
        "Physical Books",
      );
    if (eBooksData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(eBooksData.map(formatEBookRow)),
        "E-Books",
      );
    if (issuedData.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(issuedData.map(formatIssuedRow)),
        "Issued Books",
      );
    if (lowStockBooks.length)
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(lowStockBooks.map(formatBookRow)),
        "Low Stock",
      );
    if (!wb.SheetNames.length) {
      toast.error("No data available");
      return;
    }
    writeWorkbook(wb, `Library_MasterReport_${today}.xlsx`);
  };

  const reportAll = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(filteredData.map(getFormatter())),
      DATASETS[activeDataset].label.slice(0, 31),
    );
    writeWorkbook(
      wb,
      `Library_${DATASETS[activeDataset].label.replace(/ /g, "_")}_${today}.xlsx`,
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT DEFINITIONS LIST
  // ─────────────────────────────────────────────────────────────────────────
  const reports = [
    // Books
    { cat: "books", label: "All Books", action: reportBooksAll },
    { cat: "books", label: "Class Wise", action: reportBooksClassWise },
    { cat: "books", label: "Subject Wise", action: reportBooksSubjectWise },
    { cat: "books", label: "Book Type Wise", action: reportBooksTypeWise },
    { cat: "books", label: "Publisher Wise", action: reportBooksPublisher },
    { cat: "books", label: "Author Wise", action: reportBooksAuthor },
    { cat: "books", label: "Language Wise", action: reportBooksLanguage },
    { cat: "books", label: "Available Books", action: reportBooksAvailable },
    {
      cat: "books",
      label: "Fully Issued Books",
      action: reportBooksFullyIssued,
    },
    // E-Books
    { cat: "ebooks", label: "All E-Books", action: reportEBooksAll },
    { cat: "ebooks", label: "Class Wise", action: reportEBooksClassWise },
    { cat: "ebooks", label: "Subject Wise", action: reportEBooksSubjectWise },
    { cat: "ebooks", label: "Book Type Wise", action: reportEBooksTypeWise },
    { cat: "ebooks", label: "Language Wise", action: reportEBooksLanguage },
    // Issued / Return
    { cat: "issued", label: "All Issued Books", action: reportIssuedAll },
    { cat: "issued", label: "Issued Today", action: reportIssuedToday },
    { cat: "issued", label: "Pending Return", action: reportIssuedPending },
    { cat: "issued", label: "Returned Books", action: reportIssuedReturned },
    { cat: "issued", label: "Overdue Books", action: reportIssuedOverdue },
    { cat: "issued", label: "Class Wise", action: reportIssuedClassWise },
    { cat: "issued", label: "Date Range", action: reportIssuedDateRange },
    // Stock
    { cat: "stock", label: "Low Stock Alert", action: reportLowStock },
    { cat: "stock", label: "Stock Summary", action: reportStockSummary },
    { cat: "stock", label: "Available Books", action: reportBooksAvailable },
    {
      cat: "stock",
      label: "Fully Issued Books",
      action: reportBooksFullyIssued,
    },
    // Analytics
    {
      cat: "analytics",
      label: "Library Summary",
      action: reportLibrarySummary,
    },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              {/* ══════════════ LEFT COLUMN ══════════════ */}
              <div className="col-lg-8">
                {/* ── HERO HEADER ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-book me-1"></i> Reports &amp;
                        Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Library Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter &amp; download every library report —
                          all in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(DATASETS).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const count = datasetMap[key]?.data?.length || 0;
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${ds.color} text-white`
                                    : `bg-${ds.color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {count.toLocaleString("en-IN")}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── FILTERS ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedClass("");
                        setSelectedSubject("");
                        setSelectedType("");
                        setSelectedLang("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search book ID, title, author, publisher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Class & Subject */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Class &amp; Subject
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-mortarboard-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Classes"
                            value={
                              selectedClass
                                ? { value: selectedClass, label: selectedClass }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedClass(opt ? opt.value : "")
                            }
                            options={classOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-book-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Subjects"
                            value={
                              selectedSubject
                                ? {
                                    value: selectedSubject,
                                    label: selectedSubject,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedSubject(opt ? opt.value : "")
                            }
                            options={subjectOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Book Type & Language */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Book Type &amp; Language
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-tag-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Types"
                            value={
                              selectedType
                                ? { value: selectedType, label: selectedType }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedType(opt ? opt.value : "")
                            }
                            options={bookTypeOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-translate text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Languages"
                            value={
                              selectedLang
                                ? { value: selectedLang, label: selectedLang }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedLang(opt ? opt.value : "")
                            }
                            options={languageOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Date Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── LIVE SNAPSHOT ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut — Available vs Issued */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#198754"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.totalQty ? (snapshot.availableQty / snapshot.totalQty) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#ffc107"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.totalQty ? (snapshot.issuedQty / snapshot.totalQty) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${snapshot.totalQty ? -(snapshot.availableQty / snapshot.totalQty) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">
                            Total Copies
                          </p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {snapshot.totalQty.toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-bookmark-check-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Available
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.availableQty.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.totalQty
                                ? Math.round(
                                    (snapshot.availableQty /
                                      snapshot.totalQty) *
                                      100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-bookmark-x-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Issued Out
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.issuedQty.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.overdueIssued} overdue
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-laptop text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              E-Books
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.totalEBooks.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.totalPhysical} physical titles
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-dark-soft rounded-4 p-4 h-100">
                            <i className="bi bi-exclamation-triangle-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Low Stock
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.lowStock.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              ≤{LOW_STOCK_THRESHOLD} copies left
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ══════════════ RIGHT COLUMN ══════════════ */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports &amp; Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Library
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  {/* Avatar + count */}
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  {/* Title row */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {DATASETS[activeDataset]?.label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={reportAll}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {REPORT_CATEGORIES.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#libReportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="libReportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="libReportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick action cards — Overview */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportLibrarySummary}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Library Stats
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Modules
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportStockSummary}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Stock Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Class Wise Qty Report
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportIssuedOverdue}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Overdue
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Books</p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportLowStock}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Low Stock
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Alert</p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportIssuedPending}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Pending
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Return</p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportEBooksAll}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            E-Books
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Records
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default LibraryReports;
